import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileText, Trash2, ExternalLink, ImageIcon, Star } from "lucide-react";
import { api, errorMessage, fileUrl } from "../../lib/api";
import type { FileRec } from "../../lib/types";
import { fmtBytes, fmtDateTime } from "../../lib/utils";
import { Button } from "../core/Button";
import { Select } from "../core/Field";
import { useMeta } from "./TenantSelect";

export function FilesPanel({ vehicleId, kind, readOnly, profileFileId, onSetProfile }: { vehicleId: string; kind: "document" | "image"; readOnly?: boolean; profileFileId?: string | null; onSetProfile?: (fileId: string) => void }) {
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const ref = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState("Grant / VOC");
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery<FileRec[]>({ queryKey: ["files", vehicleId, kind], queryFn: async () => (await api.get("/files", { params: { vehicle_id: vehicleId, kind } })).data });

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("vehicle_id", vehicleId);
        fd.append("kind", kind);
        if (kind === "document") fd.append("doc_type", docType);
        fd.append("file", f);
        await api.post("/files/upload", fd);
      }
      toast.success(`${files.length} file(s) uploaded`);
      qc.invalidateQueries({ queryKey: ["files", vehicleId] });
      qc.invalidateQueries({ queryKey: ["vehicle-history", vehicleId] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = async (f: FileRec) => {
    if (!window.confirm(`Remove ${f.original_filename}?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File removed");
      qc.invalidateQueries({ queryKey: ["files", vehicleId] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div data-testid={`files-panel-${kind}`}>
      {!readOnly && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {kind === "document" && <Select data-testid="doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className="w-52">{(meta?.doc_types || []).map((d) => <option key={d}>{d}</option>)}</Select>}
          <input ref={ref} type="file" multiple accept={kind === "image" ? "image/*" : "application/pdf,image/*"} className="hidden" data-testid={`${kind}-file-input`} onChange={(e) => upload(e.target.files)} />
          <Button variant="secondary" loading={busy} onClick={() => ref.current?.click()} data-testid={`upload-${kind}-button`}><Upload className="h-4 w-4" />Upload {kind === "image" ? "photos" : "documents"}</Button>
          <span className="text-[11px] text-slate-500">{kind === "image" ? "JPG/PNG/WebP" : "PDF/JPG/PNG"} · ≤10MB · stored in POMEN object storage</span>
        </div>
      )}
      {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
      {data && data.length === 0 && <div className="card-crisp py-12 text-center text-sm text-slate-500">{kind === "image" ? <ImageIcon className="mx-auto mb-2 h-6 w-6 text-slate-300" /> : <FileText className="mx-auto mb-2 h-6 w-6 text-slate-300" />}No {kind === "image" ? "photos" : "documents"} uploaded yet.</div>}
      {kind === "image" ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {(data || []).map((f) => (
            <figure key={f.id} className={`card-crisp group relative overflow-hidden ${profileFileId === f.id ? "ring-2 ring-[color:var(--gold)]" : ""}`} data-testid={`image-card-${f.id}`}>
              <img src={fileUrl(f.id)} alt={f.original_filename} className="aspect-[4/3] w-full object-cover" />
              {profileFileId === f.id && <span className="absolute left-2 top-2 rounded-full bg-[color:var(--gold)] px-2 py-0.5 text-[10px] font-bold uppercase text-slate-900" data-testid={`profile-photo-badge-${f.id}`}><Star className="mr-0.5 inline h-3 w-3" />Profile</span>}
              <figcaption className="flex items-center justify-between gap-1 px-2 py-1.5 text-[11px] text-slate-600">
                <span className="truncate">{f.original_filename}</span>
                {!readOnly && <span className="flex shrink-0 items-center gap-1">
                  {onSetProfile && profileFileId !== f.id && <button onClick={() => onSetProfile(f.id)} data-testid={`set-profile-photo-${f.id}`} title="Set as profile photo" className="text-slate-500 hover:text-[color:var(--brand)]"><Star className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => remove(f)} data-testid={`delete-file-${f.id}`} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </span>}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        data && data.length > 0 && (
          <div className="card-crisp overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="th">Document</th><th className="th">Type</th><th className="th">Size</th><th className="th">Uploaded</th><th className="th"></th></tr></thead>
              <tbody>
                {data.map((f) => (
                  <tr key={f.id} className="row-hover" data-testid={`document-row-${f.id}`}>
                    <td className="td"><span className="flex items-center gap-2 font-semibold text-slate-800"><FileText className="h-4 w-4 text-slate-400" />{f.original_filename}</span></td>
                    <td className="td text-xs">{f.doc_type || "—"}</td>
                    <td className="td text-xs">{fmtBytes(f.size)}</td>
                    <td className="td text-xs">{fmtDateTime(f.created_at)}</td>
                    <td className="td text-right"><div className="flex justify-end gap-1">
                      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" data-testid={`open-file-${f.id}`} className="rounded p-1.5 text-slate-600 hover:bg-slate-100"><ExternalLink className="h-3.5 w-3.5" /></a>
                      {!readOnly && <button onClick={() => remove(f)} data-testid={`delete-file-${f.id}`} className="rounded p-1.5 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
